import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Box, CircularProgress, Tab, Tabs, Typography, useTheme} from '@mui/material'
import ChartCard from 'src/components-new/data/ChartCard'
import EmptyState from 'src/components-new/layout/EmptyState'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import AdherenceTherapyPanel from 'src/pages-new/patient/tabs/AdherenceTherapyPanel'
import {isSecondaryAdherenceEnabled, resolveAdherenceDoctorConfig, resolveAdherenceTabs} from 'src/utils/adherenceCharts'

interface AnalyticsTabProps {
  patientId: string
}

const COLLECTIONS = {
  VITAL_SIGNS: 'vital-signs-collection',
  THERAPEUTIC: 'therapeutic-parameters-collection'
}

type SubTab = 'vital' | 'therapeutic' | 'adherence'

interface ChartSeries {
  id: string
  name: string
  unitMeasure?: string
  chartType?: string
  data: Array<[string | number, number]>
  color?: string
}

// Per-metric override from the PSP configurator. feedbacksFormatKey matches the
// series' item.id from feedbacks-chart-values (e.g. 'height', 'weight', 'nyha').
interface AnalyticsChartConfig {
  subactivityTypeId: string
  feedbacksFormatKey: string
  chartType?: string
  color?: string
  title?: string
}

function formatCompilationLabel(value?: number): string {
  return value === undefined ? '' : `T${value}`
}

// Guards a single chart card: if Highcharts throws while mounting/updating a
// malformed series (e.g. an unexpected chartType/data shape from the backend),
// only this card degrades instead of taking down the whole page (React 17 has
// no default boundary, so an uncaught render error blanks the entire app).
class ChartErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean}> {
  state = {hasError: false}

  static getDerivedStateFromError() {
    return {hasError: true}
  }

  componentDidCatch(error: unknown) {
    // eslint-disable-next-line no-console
    console.error('Impossibile visualizzare il grafico', error)
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{py: 4, textAlign: 'center'}}>
          <Typography variant="body2" color="error">
            Impossibile visualizzare questo grafico.
          </Typography>
        </Box>
      )
    }
    return this.props.children
  }
}

function getValidSeries(series: ChartSeries[]): ChartSeries[] {
  return series.filter(item => Array.isArray(item.data) && item.data.length > 0)
}

// Mirrors the legacy "Intervallo di compilazioni" filter: it only makes sense for
// compilation-indexed series (e.g. a questionnaire score repeated over T0, T1, T2…),
// not for continuous measurements tied to real calendar dates (height/weight/temperature),
// which are filtered via the "Data" range instead. Series without an explicit
// chartType are treated as calendar-based and left untouched.
function sliceByCompilationInterval(series: ChartSeries[], interval?: [number, number]): ChartSeries[] {
  if (!interval) return series
  const [start, end] = interval
  return series.map(item => {
    const isCompilationIndexed = item.chartType === 'column' || item.chartType === 'line'
    if (!isCompilationIndexed || !Array.isArray(item.data)) return item
    return {...item, data: item.data.slice(start, end + 1)}
  })
}

type ChartOverride = Pick<AnalyticsChartConfig, 'color' | 'chartType'>

function ChartsGrid({series, chartConfigByKey}: {series: ChartSeries[]; chartConfigByKey: Record<string, ChartOverride>}): JSX.Element {
  const theme = useTheme()

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: 2
      }}
    >
      {series.map(item => {
        const chartTitle = item.unitMeasure
          ? `${item.name} [${item.unitMeasure}]`
          : item.name

        // Defend against malformed tuples from the backend (e.g. a bare value instead
        // of a [date, value] pair) — destructuring a non-array entry would throw and,
        // with no error boundary further up, blank the whole page.
        const points = item.data.filter((point): point is [string | number, number] => Array.isArray(point) && point.length === 2)

        const categories = points.map(([ts]) => {
          const date = new Date(ts)
          return isNaN(date.getTime())
            ? String(ts ?? '')
            : date.toLocaleDateString('it-IT', {day: '2-digit', month: 'short', year: '2-digit'})
        })
        const values = points.map(([, v]) => v)
        const override = chartConfigByKey[item.id]
        // The PSP configurator's chartType wins over the one the backend attaches to
        // the feedback data; only 'line' and 'column' are supported here — anything
        // else falls back to 'line' rather than being fed straight into Highcharts,
        // which throws (and blanks the whole panel) on an unrecognized chart.type.
        const rawChartType = override?.chartType ?? item.chartType
        const isColumn = rawChartType === 'column' || rawChartType === 'bar'
        const chartType = isColumn ? 'column' as const : 'line' as const

        const options: Highcharts.Options = {
          chart: {type: chartType, height: 220},
          title: {text: undefined},
          xAxis: {categories, labels: {style: {fontSize: '10px'}}},
          yAxis: {title: {text: undefined}},
          // Highcharts merges options by key, and an explicit `undefined` here
          // overwrites (rather than skips) its internal plotOptions default —
          // it must be omitted entirely for non-column charts, not set to undefined.
          ...(isColumn ? {
            plotOptions: {
              column: {
                pointPadding: 0,
                borderWidth: 0,
                dataLabels: {inside: true, enabled: true}
              }
            }
          } : {}),
          series: [{
            type: chartType,
            name: item.name,
            data: values,
            // Use the color set for this metric in the PSP configurator, if any;
            // otherwise fall back to the PSP's primary brand color as default.
            // (Deliberately ignores item.color from the API — that's raw feedback
            // data, not the configurator, and defaults to green server-side.)
            color: override?.color ?? theme.palette.primary.main,
            ...(isColumn ? {dataLabels: {enabled: true}} : {})
          }],
          legend: {enabled: false},
          credits: {enabled: false},
          tooltip: {
            formatter() {
              return `<b>${this.x}</b><br/>${this.series.name}: ${this.y}`
            }
          }
        }

        return (
          <ChartErrorBoundary key={item.id ?? item.name}>
            <ChartCard
              title={chartTitle}
              options={options}
            />
          </ChartErrorBoundary>
        )
      })}
    </Box>
  )
}

export default function AnalyticsTab({patientId}: AnalyticsTabProps): JSX.Element {
  const analyticsConfig: AnalyticsChartConfig[] = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration?.analytics
  )
  const chartConfigByKey = useMemo(() => {
    const entries = Array.isArray(analyticsConfig) ? analyticsConfig : []
    return entries.reduce<Record<string, ChartOverride>>((acc, config) => {
      if (config?.feedbacksFormatKey) acc[config.feedbacksFormatKey] = {color: config.color, chartType: config.chartType}
      return acc
    }, {})
  }, [analyticsConfig])

  const analyticsAdherenceConfig = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration?.analyticsAdherence
  )
  const adherenceConfig = useMemo(() => resolveAdherenceDoctorConfig(analyticsAdherenceConfig), [analyticsAdherenceConfig])
  const adherenceEnabled = adherenceConfig.show
  // the secondary therapies are only worth loading when their section can be shown at all
  const secondaryAdherenceEnabled = adherenceEnabled && isSecondaryAdherenceEnabled(adherenceConfig.sections)

  const [activeTab, setActiveTab] = useState<SubTab>('vital')
  const [vitalSeries, setVitalSeries] = useState<ChartSeries[]>([])
  const [therapeuticSeries, setTherapeuticSeries] = useState<ChartSeries[]>([])
  const [dateFilter, setDateFilter] = useState<{startDate?: string; endDate?: string}>({})
  const [compilationInterval, setCompilationInterval] = useState<[number, number] | undefined>(undefined)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  // The "Altre terapie" section of "Aderenza terapia" only exists for a patient who has some
  // secondary therapies (same rule as the patient's own Analytics); undefined while still unknown
  const [hasSecondaryTherapies, setHasSecondaryTherapies] = useState<boolean>()

  useEffect(() => {
    // A tab or section disabled in the configuration is never shown, whatever the patient has
    if (!secondaryAdherenceEnabled) {
      setHasSecondaryTherapies(false)
      return undefined
    }
    let cancelled = false
    setHasSecondaryTherapies(undefined)
    apiClient.getPatientsSecondaryTherapies(patientId, () => undefined)
      .then((therapies: unknown) => {
        if (!cancelled) setHasSecondaryTherapies(Array.isArray(therapies) && therapies.length > 0)
      })
      .catch(() => {
        if (!cancelled) setHasSecondaryTherapies(false)
      })
    return () => { cancelled = true }
  }, [patientId, secondaryAdherenceEnabled])

  const fetchCharts = useCallback(() => {
    setStatus(STATUS.PENDING)
    Promise.all([
      apiClient.getPatientsFeedbacksChartValues(COLLECTIONS.VITAL_SIGNS, patientId, dateFilter, () => undefined),
      apiClient.getPatientsFeedbacksChartValues(COLLECTIONS.THERAPEUTIC, patientId, dateFilter, () => undefined)
    ])
      .then(([vitalData, therapeuticData]) => {
        setVitalSeries(Array.isArray(vitalData) ? vitalData : [])
        setTherapeuticSeries(Array.isArray(therapeuticData) ? therapeuticData : [])
        setStatus(STATUS.FULFILLED)
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [patientId, dateFilter])

  useEffect(() => {
    fetchCharts()
  }, [fetchCharts])

  const handleFiltersSubmit = (results: FilterResult[]) => {
    const dateResult = results.find(item => item.property === 'date')
    const intervalResult = results.find(item => item.property === 'compilationInterval')

    setDateFilter({
      startDate: dateResult?.value?.[0] ?? undefined,
      endDate: dateResult?.value?.[1] ?? undefined
    })
    const intervalValue = intervalResult?.value
    setCompilationInterval(
      Array.isArray(intervalValue) ? [intervalValue[0], intervalValue[1]] : undefined
    )
  }

  const validVitalSeries = getValidSeries(vitalSeries)
  const validTherapeuticSeries = getValidSeries(therapeuticSeries)
  const hasVitalData = validVitalSeries.length > 0
  const hasTherapeuticData = validTherapeuticSeries.length > 0
  const hasAnyData = hasVitalData || hasTherapeuticData
  // Sections of "Aderenza terapia" (Terapie / Altre terapie) enabled in the configuration; the tab
  // exists only when it is enabled and has at least one of them
  const adherenceTabs = useMemo(
    () => (adherenceEnabled ? resolveAdherenceTabs(adherenceConfig.sections, Boolean(hasSecondaryTherapies)) : []),
    [adherenceEnabled, adherenceConfig.sections, hasSecondaryTherapies]
  )
  const hasAdherence = adherenceTabs.length > 0

  // A filter can legitimately narrow the result set down to zero (e.g. a date range
  // with no compilations). In that case hasAnyData alone would hide the Filtra button
  // along with the tabs, trapping the user with no way to reset it — so the button
  // must stay visible whenever a filter is active, even with an empty result.
  const hasActiveFilters = Boolean(dateFilter.startDate || dateFilter.endDate || compilationInterval)
  const showFiltersButton = hasAnyData || hasActiveFilters

  // Max of the "Intervallo di compilazioni" slider tracks the active sub-tab's first
  // series length, same source legacy used — good enough since compilation-indexed
  // series in a given collection share the same number of compilations.
  const activeSeriesForMax = activeTab === 'vital' ? validVitalSeries : validTherapeuticSeries
  const maxCompilation = Math.max((activeSeriesForMax[0]?.data?.length ?? 21) - 1, 0)

  const filterConfig: FilterItem[] = useMemo(() => [
    {id: 'date', label: 'Data', type: 'rangePicker'},
    {
      id: 'compilationInterval',
      label: 'Intervallo di compilazioni',
      type: 'range',
      min: 0,
      max: maxCompilation,
      step: 1,
      valueLabelFormat: formatCompilationLabel
    }
  ], [maxCompilation])

  const displayedVitalSeries = sliceByCompilationInterval(validVitalSeries, compilationInterval)
  const displayedTherapeuticSeries = sliceByCompilationInterval(validTherapeuticSeries, compilationInterval)

  // Mirrors the legacy behaviour: a sub-tab is shown only if it has data. If the
  // currently active sub-tab loses its data (new filters, new patient), fall back
  // to whichever sub-tab still has data.
  // A chart filter that narrows everything down to zero keeps the "Parametri Vitali" tab (with its
  // empty state), so the Filtra button stays reachable even when "Aderenza terapia" is the only
  // tab left with data.
  const showVitalTab = hasVitalData || (hasActiveFilters && !hasAnyData)

  // "Aderenza terapia" does not depend on those charts nor on their filters: it has its own.
  const availableTabs = useMemo(() => {
    const result: SubTab[] = []
    if (showVitalTab) result.push('vital')
    if (hasTherapeuticData) result.push('therapeutic')
    if (hasAdherence) result.push('adherence')
    return result
  }, [showVitalTab, hasTherapeuticData, hasAdherence])

  const isLoading = status === STATUS.PENDING || hasSecondaryTherapies === undefined

  // Only once everything is loaded: while the charts are still loading "Aderenza terapia" would be
  // the only tab with data and would steal the selection from "Parametri Vitali".
  useEffect(() => {
    if (!isLoading && availableTabs.length > 0 && !availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0])
    }
  }, [isLoading, activeTab, availableTabs])

  if (isLoading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
        <CircularProgress />
      </Box>
    )
  }

  if (status === STATUS.REJECTED && !hasAdherence) {
    return (
      <Box sx={{py: 4, textAlign: 'center'}}>
        <Typography variant="body2" color="error">
          Impossibile caricare i grafici.
        </Typography>
      </Box>
    )
  }

  const isChartsTab = activeTab !== 'adherence'

  return (
    <Box>
      {status === STATUS.REJECTED && (
        <Box sx={{py: 2, textAlign: 'center'}}>
          <Typography variant="body2" color="error">
            Impossibile caricare i grafici.
          </Typography>
        </Box>
      )}
      {showFiltersButton && isChartsTab && (
        <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 2}}>
          <Filters config={filterConfig} onSubmit={handleFiltersSubmit} />
        </Box>
      )}

      {availableTabs.length === 0 ? (
        hasActiveFilters ? (
          <EmptyState title="Nessun risultato" description="Nessun dato per i filtri selezionati. Modifica o azzera i filtri per vedere tutti i dati." />
        ) : (
          <EmptyState title="Nessun dato disponibile" description="Non ci sono dati grafici per questo paziente." />
        )
      ) : (
        <>
          <Tabs
            value={activeTab}
            onChange={(_e, val: SubTab) => setActiveTab(val)}
            sx={{
              mb: 3,
              '& .MuiTab-root': {fontSize: 13, textTransform: 'none', minHeight: 40},
              '& .MuiTabs-indicator': {height: 2}
            }}
          >
            {showVitalTab && <Tab value="vital" label="Parametri Vitali" />}
            {hasTherapeuticData && <Tab value="therapeutic" label="Parametri Terapeutici" />}
            {hasAdherence && <Tab value="adherence" label={adherenceConfig.label} />}
          </Tabs>

          {activeTab === 'vital' && !hasVitalData && showVitalTab && (
            <EmptyState title="Nessun risultato" description="Nessun dato per i filtri selezionati. Modifica o azzera i filtri per vedere tutti i dati." />
          )}
          {activeTab === 'vital' && hasVitalData && (
            displayedVitalSeries.some(item => item.data.length > 0)
              ? <ChartsGrid series={displayedVitalSeries} chartConfigByKey={chartConfigByKey} />
              : <EmptyState title="Nessun dato per l'intervallo selezionato" description="Modifica l'intervallo di compilazioni per visualizzare i grafici." />
          )}
          {activeTab === 'therapeutic' && hasTherapeuticData && (
            displayedTherapeuticSeries.some(item => item.data.length > 0)
              ? <ChartsGrid series={displayedTherapeuticSeries} chartConfigByKey={chartConfigByKey} />
              : <EmptyState title="Nessun dato per l'intervallo selezionato" description="Modifica l'intervallo di compilazioni per visualizzare i grafici." />
          )}
          {activeTab === 'adherence' && hasAdherence && (
            <AdherenceTherapyPanel
              patientId={patientId}
              tabs={adherenceTabs}
              visibleFilters={adherenceConfig.filtersConfig}
              labels={adherenceConfig.labels}
            />
          )}
        </>
      )}
    </Box>
  )
}